<template>
    <BaseForm @submit.prevent>
        <template #fields>
            <TextField
                :value="values.nameShort"
                :label="$t('name-short')"
                :error-messages="errors.nameShort"
                :required="true"
                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameShort', true)"
            />
            <TextField
                :value="values.nameLong"
                :label="$t('name-long')"
                :error-messages="errors.nameLong"
                :required="true"
                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameLong', true)"
            />
            <ComboboxField
                :value="values.category"
                :items="getExternalEventSeriesCategoryOptions()"
                :label="$t('external-event-category')"
                :error-messages="errors.category"
                :required="true"
                @change="setFieldValue('category', $event)"
                @blur="setFieldTouched('category', true)"
            >
            </ComboboxField>
            <RadioButtonGroup
                :label="$t('external-event-national-radio-label')"
                name="national-choice"
                :options="nationalOptions"
                :is-required="true"
                @change="
                    setFieldValue('national', ($event.target as HTMLInputElement).value === 'national' ? true : false)
                "
                @blur="setFieldTouched('national', true)"
            />
        </template>
        <template #buttons>
            <Button v-if="isTouched" size="Base" variant="Danger-Light" @click="onDiscard">
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('discard') }}
            </Button>
            <Button v-else size="Base" variant="Neutral-Light" @click="$router.back">
                <template #icon>
                    <CloseIcon class="size-5" />
                </template>
                {{ $t('abort') }}
            </Button>
            <Button
                size="Base"
                variant="Info-Strong"
                :disabled-explanation="saveButtonDisabledExplanation"
                type="submit"
                @click="onSave"
            >
                <template #icon>
                    <AllDoneIcon class="size-5" />
                </template>
                {{ $t('create') }}
            </Button>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import Button from '@/components/Button/Button.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import ComboboxField from '@/components/Form/ComboboxField.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import TextField from '@/components/Form/TextField.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import { useExternalEventSeriesCreate } from '@/external_event/composables/useExternalEventSeriesCreate';
import { ExternalEventSeriesCreateSchema } from '@/external_event/schemas';
import { useForm } from '@/forms/composables/useForm';
import { $t } from '@/plugins/fluent';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { getExternalEventSeriesCategoryOptions } from '../externalEvent';

const router = useRouter();

const {
    values,
    errors,
    saveButtonDisabledExplanation,
    isTouched,
    setFieldTouched,
    setFieldValue,
    validate,
    onDiscard,
} = useForm(
    ExternalEventSeriesCreateSchema,
    computed(() => ({
        nameShort: '',
        nameLong: '',
        category: null,
        national: true,
    })),
);

const { create } = useExternalEventSeriesCreate(values);

const nationalOptions = [
    { id: 'national', label: $t('external-event-national-choice'), isChecked: true, value: 'national' },
    { id: 'international', label: $t('external-event-international-choice'), isChecked: false, value: 'international' },
];

async function onSave() {
    const valid = await validate();
    if (valid) {
        const { id } = await create();
        if (id) {
            router.push({ name: 'external-event-series', params: { seriesId: id } });
        }
    }
}
</script>
